import CustomTooltip from '../../../shared/CustomTooltip';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { MessagingUser } from '../../../types/user';
import { AddCircle, RemoveModerator } from '@mui/icons-material';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import DeleteModal from './DeleteModal';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import UserModal from './UserModal';

interface UserTableProps {
    users: MessagingUser[];
}

const UserTable = ({ users }: UserTableProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isIpad = useMediaQuery(theme.breakpoints.down('lg'));

    const [formOpen, setFormOpen] = useState(false);

    const [pendingDelete, setPendingDelete] = useState<MessagingUser | null>(
        null,
    );
    const [pendingEdit, setPendingEdit] = useState<MessagingUser | null>(null);
    const [confirmationOpen, setConfirmationOpen] = useState(false);

    const handleRemove = async (
        e: React.MouseEvent<HTMLButtonElement>,
        user: MessagingUser,
    ) => {
        // prevent row click
        e.stopPropagation();

        setPendingDelete(user);
        setConfirmationOpen(true);
    };

    const handleOpenForm = (user: MessagingUser) => {
        setPendingEdit(user);
        setFormOpen(true);
    };

    const handleCloseForm = () => {
        setPendingEdit(null);
        setFormOpen(false);
    };

    const handleCloseDelete = () => {
        setPendingDelete(null);
        setConfirmationOpen(false);
    };

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant='body3' sx={{ color: 'text.primary' }}>
                    In-app messaging makes communication effortless,
                    streamlining bookings, inquiries, and updates. Easily assign
                    support users for a seamless experience!
                </Typography>
                {isMobile || isIpad ? (
                    <CustomIconButton
                        variant='contained'
                        color='primary'
                        onClick={() => setFormOpen(true)}
                    >
                        <AddCircle />
                    </CustomIconButton>
                ) : (
                    <CustomButton
                        variant='contained'
                        color='primary'
                        startIcon={<AddCircle />}
                        onClick={() => setFormOpen(true)}
                    >
                        Add User
                    </CustomButton>
                )}
            </Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell padding='checkbox' />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow
                                key={user.id}
                                onClick={() => handleOpenForm(user)}
                                sx={{ cursor: 'pointer' }}
                                hover
                            >
                                <TableCell>
                                    {user.firstName} {user.lastName}
                                </TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell padding='checkbox'>
                                    <CustomTooltip title='Revoke Access'>
                                        <CustomButton
                                            color='error'
                                            variant='outlined'
                                            size='small'
                                            startIcon={<RemoveModerator />}
                                            onClick={(
                                                e: React.MouseEvent<HTMLButtonElement>,
                                            ) => handleRemove(e, user)}
                                        >
                                            Revoke
                                        </CustomButton>
                                    </CustomTooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {pendingDelete !== null && (
                <DeleteModal
                    open={confirmationOpen}
                    onClose={handleCloseDelete}
                    user={pendingDelete}
                    messagingUsers={users}
                />
            )}
            <UserModal
                open={formOpen}
                onClose={handleCloseForm}
                messagingUsers={users}
                user={pendingEdit}
            />
        </>
    );
};

export default UserTable;
